import { useUserStore } from '@roc/feature-app-core';
import { useLoanStore, useManualQuoteStore } from '@roc/feature-loans';
import { observer } from 'mobx-react';
import { CustomCard } from '../customCard';
import { Box, Checkbox, Chip, FormControlLabel, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { LoanDetailsPaper } from '../loanDetailsPaper';
import { useEffect, useState } from 'react';
import { Button, ConfirmDialog, StandardDialog } from '@roc/ui';
import { useLoanDetailsInfoItemStyles } from '../loanDetailsCustomCard';
import { ManualQuoteReviseTerms } from '../bridge/manualQuoteReviseTerms';
import { isActiveManualQuote, isFixAndFlip, isGroundUp, isStabilizedBridge } from '@roc/feature-utils';
import { ManualQuoteDeclineDialog } from '../bridge/manualQuoteDeclineDialog';
import { useLoanRoutes } from '@roc/feature-loans-routes-config';
import { formatPercentageValue, QuoteTypeStore, useOneToolStore } from '@roc/feature-one-tool';
import { useHistory } from 'react-router';
import { Document } from '@roc/feature-types';
import { WithDocumentActions } from '@roc/feature-documents';

const useStyles = makeStyles((theme: Theme) => ({
  infoMessage: {
    color: '#626161',
    fontSize: '13px',
  },
  card: {
    border: 0,
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(2.5),
  },
  textValue: {
    textAlign: 'center',
    width: '100%',
    fontWeight: 'bold',
  },
  value: {
    textAlign: 'center',
    width: '100%',
  },
  titleValue: {
    alignSelf: 'center',
    fontWeight: 'bold',
  },
  primaryButton: {
    border: `solid 1px ${theme.palette.primary.main}`,
    padding: '4px 14px',
    fontSize: 15,
    marginLeft: '2.5px',
    marginRight: '2.5px',
    '&:disabled': {
      border: 'none'
    },
  },
  textDisabled: {
    color: theme.palette.text.disabled,
  },

  declineButton: {
    color: '#d94451',
    border: `solid 1px #d94451`,
    backgroundColor: '#FFFFFF',
    fontSize: 15,
    marginLeft: '2.5px',
    marginRight: '2.5px',
    '&:disabled': {
      border: 'none'
    },
    '&:hover': {
      backgroundColor: '#FFFFFF',
    }
  },
}));

const statusConfig = {
  Rejected: {
    bgColor: "#ffe5e5",
    message: "Declined Terms",
  },
  Accepted: {
    bgColor: "#e5ffe8",
    message: "Revised Terms Accepted",
  },
  InProgress: {
    bgColor: "#fff9e5",
    message: "Submitted For Review",
  },
  Created: {
    bgColor: "#fff9e5",
    message: "Submitted For Review",
  },
  Requested: {
    bgColor: "#fff9e5",
    message: "Submitted For Review",
  },

};

export const ManualQuoteTermCard = observer(({ document }: { document?: Document }) => {
  const { loanStore } = useLoanStore();
  const { userStore } = useUserStore();
  const { push } = useHistory();
  const { loanDetails } = loanStore;
  const classes = useStyles();
  const { oneToolStore } = useOneToolStore();
  const { fixFlipQuoteStore, groundUpQuoteStore, stabilizedBridgeQuoteStore } = oneToolStore;
  const [showProposedValues, setShowProposedValues] = useState(true);
  const [showRevisedValues, setShowRevisedValues] = useState(false);
  const [showAllValues, setShowAllValues] = useState(false);
  const { manualQuoteStore } = useManualQuoteStore();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusTitle, setStatusTitle] = useState(null);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const { loanRoutesConfig } = useLoanRoutes();

  useEffect(() => {
    if (!loanDetails?.loanId) return;
    loanStore.fetchLoanChangeRequests(loanDetails?.loanId, () => {
      if (isFixAndFlip(loanDetails?.loanSubType)) {
        fixFlipQuoteStore.loadQuoteToEdit(loanDetails?.loanId, true, loanStore?.lastFetchedCustomQuote);
      }
      if (isGroundUp(loanDetails?.loanSubType)) {
        groundUpQuoteStore.loadQuoteToEdit(loanDetails?.loanId, true, loanStore?.lastFetchedCustomQuote);
      }
      if (isStabilizedBridge(loanDetails?.loanSubType)) {
        stabilizedBridgeQuoteStore.loadQuoteToEdit(loanDetails?.loanId, true, loanStore?.lastFetchedCustomQuote);
      }
      setStatusTitle(title())
    })
  }, [loanDetails?.loanId]);

  useEffect(() => {
    if (loanStore?.lastFetchedCustomQuote?.action === 'Rejected') {
      setShowProposedValues(false)
      setShowRevisedValues(true)
      setShowAllValues(false)
    } else {
      setShowProposedValues(false)
      setShowRevisedValues(false)
      setShowAllValues(true)
    }
    setStatusTitle(title())
  }, [loanStore?.lastFetchedCustomQuote]);

  const title = () => {
    const config = loanStore?.lastFetchedCustomQuote ? statusConfig[loanStore?.lastFetchedCustomQuote?.action] : statusConfig["InProgress"];

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        padding: "6px 10px",
        borderRadius: "6px",
      }}>
        <span>Manual Quote Terms</span>

        <Grid item xs={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box>
            <Typography
              variant="body2"
              style={{ backgroundColor: config?.bgColor, borderStyle: 'solid', borderWidth: 1, borderColor: '#FFF4E5', padding: '4px 14px', borderRadius: 10 }}
            >
              {config?.message}
            </Typography>
          </Box>
        </Grid>
      </div>
    );
  };

  const handleSubmit = () => {
    let quoteData;
    if (isFixAndFlip(loanDetails?.loanSubType)) {
      quoteData = fixFlipQuoteStore.getQuoteData()
    }
    if (isGroundUp(loanDetails?.loanSubType)) {
      quoteData = groundUpQuoteStore.getQuoteData()
    }
    if (isStabilizedBridge(loanDetails?.loanSubType)) {
      quoteData = stabilizedBridgeQuoteStore.getQuoteData()
    }
    manualQuoteStore.saveReviseTerms(quoteData, userStore.userInformation);
    setDialogOpen(false);
  }

  const handleDecline = () => {
    if (!manualQuoteStore.validateDeclineReason()) {
      return;
    }
    const quoteData = {
      loanId: loanStore?.loanDetails?.loanId,
      source: manualQuoteStore.portal,
    }
    manualQuoteStore.declineManualQuote(quoteData);
    setDeclineDialogOpen(false);
  }

  const handleAccept = () => {
    let quoteData;
    let store: QuoteTypeStore;
    if (isFixAndFlip(loanDetails?.loanSubType)) {
      quoteData = fixFlipQuoteStore.getQuoteData()
      store = fixFlipQuoteStore
    }
    if (isGroundUp(loanDetails?.loanSubType)) {
      quoteData = groundUpQuoteStore.getQuoteData()
      store = groundUpQuoteStore
    }
    if (isStabilizedBridge(loanDetails?.loanSubType)) {
      quoteData = stabilizedBridgeQuoteStore.getQuoteData()
      store = stabilizedBridgeQuoteStore
    }
    manualQuoteStore.acceptManualQuote(quoteData, userStore.userInformation, () => {
      if (manualQuoteStore.portal === "cportal") {
        store.updateManualQuoteLoan(() => {
          if (manualQuoteStore.portal === "cportal") {
            push(loanRoutesConfig.loans(loanDetails?.loanId).children.dashboard.url)
          }
        });
      }
      store.saveLoanTermApprovalValue();
    });

    setDeclineDialogOpen(false);
  }

  return (
    <>
      <CustomCard title={statusTitle}>
        <LoanDetailsPaper>
          {showProposedValues &&
            <ProposedTermsValues showDisableStyles={false} />
          }
          {showRevisedValues &&
            <RevisedTermsValues />
          }
          {showAllValues &&
            <AcceptTermsValues
              setDialogOpen={() => setDialogOpen(true)}
              handleAccept={() => setAcceptDialogOpen(true)}
              handleDecline={() => setDeclineDialogOpen(true)}
              setShowRevisedValues={() => {
                setShowAllValues(false)
                setShowRevisedValues(true)
              }} />
          }
          {manualQuoteStore.portal === 'nportal' && isActiveManualQuote(loanDetails?.manualQuoteLoanDetailValues?.manualQuoteStatus) &&
            <Grid container spacing={2} justifyContent="center" className={classes.card}>
              {loanStore?.lastFetchedCustomQuote?.action !== 'Rejected' &&
                <Grid item>
                  <WithDocumentActions
                    document={document}
                    component={(props) => (
                      <Button
                        {...props}
                        style={{ width: '250px' }}
                        className={classes.declineButton}
                        hidden={true}
                        size="large"
                        variant="outlined"
                        color="primary"
                        testId="editLoanData"
                        onClick={() => setDeclineDialogOpen(true)}
                      >
                        DECLINE
                      </Button>
                    )} />
                </Grid>
              }

              {loanStore?.lastFetchedCustomQuote?.action !== 'Updated' && loanStore?.lastFetchedCustomQuote?.action !== 'Accepted' &&
                <Grid item>
                  <WithDocumentActions
                    document={document}
                    component={(props) => (
                      <Button
                        {...props}
                        style={{ width: '250px' }}
                        hidden={true}
                        size="large"
                        variant="contained"
                        color="primary"
                        testId="editLoanData"
                        onClick={() => setDialogOpen(true)}
                      >
                        REVISE
                      </Button>
                    )
                    } />
                </Grid>
              }
              {loanStore?.lastFetchedCustomQuote?.source !== 'nportal' && loanStore?.lastFetchedCustomQuote?.action !== 'Updated' &&
                <Grid item>
                  <WithDocumentActions
                    document={document}
                    component={(props) => (
                      <Button
                        {...props}
                        style={{ background: '#34A853', width: '250px' }}
                        hidden={true}
                        size="large"
                        variant="contained"
                        color="primary"
                        testId="editLoanData"
                        onClick={() => setAcceptDialogOpen(true)}
                      >
                        ACCEPT
                      </Button>
                    )
                    } />
                </Grid>
              }
            </Grid>
          }
        </LoanDetailsPaper>
      </CustomCard>

      <StandardDialog
        title={"Revise Quote Terms"}
        open={dialogOpen}
        maxWidth="xl"
        fullWidth={true}
        dialogContent={<ManualQuoteReviseTerms />}
        handleClose={() => setDialogOpen(false)}
        onClose={() => setDialogOpen(false)}
        dialogActions={
          <>
            <Button
              testId="submitReviewCancel"
              style={{ marginRight: '8px' }}
              onClick={() => setDialogOpen(false)}
              color="primary"
              variant="outlined"
            >
              CANCEL
            </Button>
            <Button
              testId="submitReviewConfirm"
              onClick={() => handleSubmit()}
              color="primary"
              variant="contained"
            >
              SUBMIT FOR REVIEW
            </Button>
          </>
        }
      />

      <StandardDialog
        title={"Decline Quote Terms"}
        open={declineDialogOpen}
        maxWidth="xl"
        fullWidth={true}
        dialogContent={<ManualQuoteDeclineDialog />}
        handleClose={() => setDeclineDialogOpen(false)}
        onClose={() => setDeclineDialogOpen(false)}
        dialogActions={
          <>
            <Button
              testId="submitReviewCancel"
              style={{ marginRight: '8px' }}
              onClick={() => setDeclineDialogOpen(false)}
              color="primary"
              variant="outlined"
            >
              CANCEL
            </Button>
            <Button
              testId="submitReviewConfirm"
              onClick={() => handleDecline()}
              color="primary"
              variant="contained"
              disabled={!manualQuoteStore.declineQuoteFormStore.form.fields.message.value?.trim()}
            >
              SUBMIT
            </Button>
          </>
        }
      />

      <ConfirmDialog
        open={acceptDialogOpen}
        handleClose={() => setAcceptDialogOpen(false)}
        handleCancel={() => setAcceptDialogOpen(false)}
        handleConfirm={() => {
          handleAccept();
          setAcceptDialogOpen(false);
        }}
        title={'Accept Manual Quote'}
        body={`Are you sure want to accept this manual quote ?`}
      />
    </>
  );
});

type proposedTermsProps = {
  showDisableStyles: boolean
}

export const ProposedTermsValues = observer(({ showDisableStyles }: proposedTermsProps) => {
  const { loanStore } = useLoanStore();
  const { loanDetails, createdCustomQuote } = loanStore;
  const classes = useStyles();

  return (
    <>
      <Grid className={classes.card} container spacing={3} wrap="nowrap" justifyContent="space-around">
        <Grid item xs="auto" className={`${classes.titleValue} ${showDisableStyles ? classes.textDisabled : ''}`}>
          <Typography variant="h5">
            Proposed Terms
          </Typography>
        </Grid>

        <Grid item xs="auto">
          <Typography variant="subtitle1" className={`${classes.textValue} ${showDisableStyles ? classes.textDisabled : ''}`}>
            Loan To Cost
          </Typography>
          <Typography variant="h5" className={`${classes.value} ${showDisableStyles ? classes.textDisabled : ''}`}>
            {formatPercentageValue(createdCustomQuote?.loanToCost)}
          </Typography>
        </Grid>

        <Grid item xs="auto">
          <Typography variant="subtitle1" className={`${classes.textValue} ${showDisableStyles ? classes.textDisabled : ''}`}>
            Construction Reserve
          </Typography>
          <Typography variant="h5" className={`${classes.value} ${showDisableStyles ? classes.textDisabled : ''}`}>
            {formatPercentageValue(createdCustomQuote?.constructionReserve)}
          </Typography>
        </Grid>

        <Grid item xs="auto">
          <Typography variant="subtitle1" className={`${classes.textValue} ${showDisableStyles ? classes.textDisabled : ''}`}>
            Total Loan to Cost
          </Typography>
          <Typography variant="h5" className={`${classes.value} ${showDisableStyles ? classes.textDisabled : ''}`}>
            {formatPercentageValue(createdCustomQuote?.totalLoanToCost)}
          </Typography>
        </Grid>

        <Grid item xs="auto">
          <Typography variant="subtitle1" className={`${classes.textValue} ${showDisableStyles ? classes.textDisabled : ''}`}>
            ARLTV
          </Typography>
          <Typography variant="h5" className={`${classes.value} ${showDisableStyles ? classes.textDisabled : ''}`}>
            {formatPercentageValue(createdCustomQuote?.arltv)}
          </Typography>
        </Grid>
      </Grid>
    </>

  );
});

export const RevisedTermsValues = observer(() => {
  const { loanStore } = useLoanStore();
  const classes = useStyles();
  const { lastFetchedCustomQuote } = loanStore;

  return (
    <>
      <Grid className={classes.card} container spacing={3} wrap="nowrap" justifyContent="space-around">

        <Grid item xs="auto" className={classes.titleValue}>
          <Typography variant="h5" >
            Revised Terms
          </Typography>
        </Grid>

        <Grid item xs="auto">
          <Typography variant="subtitle1" className={classes.textValue}>
            Loan To Cost
          </Typography>
          <Typography variant="h5" className={classes.value}>
            {formatPercentageValue(lastFetchedCustomQuote?.loanToCost)}
          </Typography>
        </Grid>

        <Grid item xs="auto">
          <Typography variant="subtitle1" className={classes.textValue}>
            Construction Reserve
          </Typography>
          <Typography variant="h5" className={classes.value}>
            {formatPercentageValue(lastFetchedCustomQuote?.constructionReserve)}
          </Typography>
        </Grid>

        <Grid item xs="auto">
          <Typography variant="subtitle1" className={classes.textValue}>
            Total Loan to Cost
          </Typography>
          <Typography variant="h5" className={classes.value}>
            {formatPercentageValue(lastFetchedCustomQuote?.totalLoanToCost)}
          </Typography>
        </Grid>

        <Grid item xs="auto">
          <Typography variant="subtitle1" className={classes.textValue}>
            ARLTV
          </Typography>
          <Typography variant="h5" className={classes.value}>
            {formatPercentageValue(lastFetchedCustomQuote?.arltv)}
          </Typography>
        </Grid>

      </Grid>
    </>

  );
});

type acceptTermsValuesProps = {
  setShowRevisedValues: () => void
  handleAccept: () => void
  handleDecline: () => void
  setDialogOpen: () => void
}

export const AcceptTermsValues = observer(({ setShowRevisedValues, handleAccept, handleDecline, setDialogOpen }: acceptTermsValuesProps) => {
  const { manualQuoteStore } = useManualQuoteStore();
  const classes = useStyles();
  const generalClasses = useLoanDetailsInfoItemStyles();
  const fieldName = 'acceptCheck';
  const field = manualQuoteStore.manualQuoteFormStore.form.fields[fieldName];
  const { loanStore } = useLoanStore();

  return (
    <>
      <ProposedTermsValues showDisableStyles={true} />
      <RevisedTermsValues />
      <Grid container direction="column" spacing={2} className={classes.card}>

        {manualQuoteStore.portal === 'cportal' && loanStore.lastFetchedCustomQuote?.action !== 'Requested' && (loanStore.lastFetchedCustomQuote?.source === 'nportal' && loanStore.lastFetchedCustomQuote?.action !== 'Accepted') &&
          <Grid item style={{ marginLeft: '55px', marginRight: '30px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.value}
                  onChange={(e, checked) =>
                    manualQuoteStore.manualQuoteFormStore.onFieldChange(fieldName, checked)
                  }
                  color="primary"
                />
              }
              label={<Typography
                className={classes.infoMessage}
                style={{ textAlign: 'center' }}
              >
                By accepting, as a third party acting on behalf of the borrower, I certify that the borrower has been informed of and agrees to the loan terms outlined on this page.
              </Typography>}
            />
          </Grid>
        }
      </Grid>

      {manualQuoteStore.portal === 'cportal' && isActiveManualQuote(loanStore?.loanDetails?.manualQuoteLoanDetailValues?.manualQuoteStatus) &&
        <Grid container spacing={2} justifyContent="center" className={classes.card}>
          {loanStore?.lastFetchedCustomQuote?.action !== 'Rejected' &&
            <Grid item>
              <Button
                variant="outlined"
                size="small"
                onClick={handleDecline}
                className={classes.declineButton}
                color="primary"
                testId="decline-loan-button"
              >
                Decline
              </Button>
            </Grid>
          }
          {loanStore.lastFetchedCustomQuote?.action != 'Requested' && loanStore.lastFetchedCustomQuote?.source === 'nportal' &&
            <><Grid item>
              <Button
                variant="contained"
                size="small"
                onClick={setDialogOpen}
                className={generalClasses.button}
                color="primary"
                testId="edit-loan-button"
              >
                Revise Terms
              </Button>
            </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAccept}
                  color="primary"
                  className={classes.primaryButton}
                  testId="accept-terms-button"
                  disabled={!field.value}
                >
                  Accept Terms
                </Button>
              </Grid>
            </>
          }
        </Grid>
      }
    </>
  );
});
